/**
 * Применение оформления к странице (тикет T18b, спецификация § 14.1–14.5).
 *
 * Ядро клиента посчитало, каким должен быть экран; здесь эти числа раскладываются по
 * переменным стилей и по признакам документа. Никаких собственных решений: если тут
 * появится «а на сайте пусть шрифт будет чуть крупнее», человек увидит на двух
 * устройствах разное, и виноват будет этот файл.
 *
 * Язык и направление письма ставятся на сам документ, а не на его середину: голосовой
 * помощник читает язык страницы, а не язык внутренней рамки, и ошибётся с произношением
 * всего экрана сразу.
 */
import type { Appearance, DeviceHints } from '@core/settings/appearance.js';

/** Что подсказало устройство прямо сейчас. */
export const deviceHints = (): DeviceHints => {
  // Совсем старый браузер без этой возможности — не повод падать: тогда подсказок просто
  // нет, и решает выбор человека.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return {};
  return {
    prefersDark: window.matchMedia('(prefers-color-scheme: dark)').matches,
    prefersReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  };
};

/** Следить за подсказками устройства: человек меняет их, не заходя в наши Настройки. */
export const watchDeviceHints = (onChange: (hints: DeviceHints) => void): (() => void) => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const queries = [
    window.matchMedia('(prefers-color-scheme: dark)'),
    window.matchMedia('(prefers-reduced-motion: reduce)'),
  ];
  const tell = (): void => onChange(deviceHints());
  for (const query of queries) query.addEventListener('change', tell);
  return () => {
    for (const query of queries) query.removeEventListener('change', tell);
  };
};

/**
 * Разложить оформление по документу. Вызывается на каждое изменение Настроек — и
 * ничего не перерисовывает: меняются только значения переменных, а разметка остаётся
 * той же самой.
 */
export const applyAppearance = (appearance: Appearance, root: HTMLElement): void => {
  const style = root.style;

  root.dataset['theme'] = appearance.theme;
  root.dataset['font'] = appearance.fontFamily;
  root.dataset['motion'] = appearance.motion;
  root.lang = appearance.language;
  root.dir = appearance.direction;

  style.setProperty('--ktm-text-scale', String(appearance.textScale));
  style.setProperty('--ktm-font-weight', String(appearance.fontWeight));
  style.setProperty('--ktm-line-height', String(appearance.lineHeight));
  style.setProperty('--ktm-space', String(appearance.spacingScale));
  style.setProperty('--ktm-target', `${appearance.minimumTarget}px`);
  style.setProperty('--ktm-motion', appearance.motion === 'reduced' ? '0.001s' : '0.15s');
  style.setProperty('--ktm-underline', appearance.underlineLinks ? 'underline' : 'none');
};
